import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { cachedGet } from "@/lib/offlineCache";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Camera, CheckCircle, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { key: "ok", label: "OK", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700" },
  { key: "mangel", label: "Mangel", cls: "border-red-500/50 bg-red-500/10 text-red-700" },
  { key: "entfaellt", label: "Entfällt", cls: "border-border bg-muted text-muted-foreground" },
  { key: "nicht_zugaenglich", label: "Nicht zugänglich", cls: "border-amber-500/50 bg-amber-500/10 text-amber-700" },
  { key: "nicht_geprueft", label: "Nicht geprüft", cls: "border-border bg-muted text-muted-foreground" },
];

export function ChecklistFillDialog({ open, onOpenChange, component, onSaved }) {
  const [checkpoints, setCheckpoints] = useState([]);
  const [state, setState] = useState({}); // cpId -> { status, bemerkung, foto_media_ids }
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !component?.kind) return;
    setLoading(true);
    setState({});
    cachedGet(`/v1/checkpoints?kind=${component.kind}`)
      .then((data) => { const cps = data || []; setCheckpoints(cps); const s = {}; cps.forEach((cp) => { s[cp.id] = { status: "ok", bemerkung: "", foto_media_ids: [] }; }); setState(s); })
      .catch(() => setCheckpoints([]))
      .finally(() => setLoading(false));
  }, [open, component]);

  const setCp = (id, patch) => setState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const addPhoto = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("category", "Prüfung");
    if (component.asset_id) fd.append("asset_id", component.asset_id);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCp(id, { foto_media_ids: [...(state[id]?.foto_media_ids || []), data.id] });
      toast.success("Foto angehängt");
    } catch (e) { toast.error("Foto-Upload fehlgeschlagen"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const results = checkpoints.map((cp) => ({
        checkpoint_id: cp.id, code: cp.code || null, beschreibung: cp.beschreibung,
        gruppe: cp.gruppe || cp.kategorie || null,
        status: state[cp.id]?.status || "nicht_geprueft",
        bemerkung: state[cp.id]?.bemerkung || null,
        foto_media_ids: state[cp.id]?.foto_media_ids || [],
      }));
      await api.post(`/v1/components/${component.id}/inspection`, { results });
      const maengel = results.filter((r) => r.status === "mangel").length;
      toast.success(`Bestandsprüfung gespeichert${maengel ? ` — ${maengel} Mangel/Mängel` : ""}`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error("Speichern fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    setBusy(false);
  };

  const counts = STATUS_OPTS.reduce((m, o) => { m[o.key] = Object.values(state).filter((v) => v.status === o.key).length; return m; }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checklist-fill-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ListChecks size={18} weight="duotone" className="text-primary" /> Bestandsprüfung
          </DialogTitle>
          <DialogDescription>
            {component?.bezeichnung} — Ist-Zustand bei der Erfassung dokumentieren (ersetzt keine wiederkehrende Wartung).
          </DialogDescription>
        </DialogHeader>

        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Lädt Prüfpunkte …</div>
          : checkpoints.length === 0
            ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="checklist-empty">Für diesen Komponententyp ist keine Prüf-Checkliste hinterlegt.</div>
            : (
              <>
                <div className="flex flex-wrap gap-1.5" data-testid="checklist-summary">
                  {STATUS_OPTS.map((o) => counts[o.key] > 0 && <Badge key={o.key} variant="outline" className={`text-[10px] ${o.cls}`}>{o.label}: {counts[o.key]}</Badge>)}
                </div>
                <div className="space-y-2">
                  {checkpoints.map((cp, i) => (
                    <div key={cp.id} className="rounded-md border border-border p-3" data-testid={`checklist-item-${i}`}>
                      <div className="mb-2 flex items-start gap-2">
                        <span className="mt-0.5 text-xs font-bold text-muted-foreground">{i + 1}.</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{cp.beschreibung}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                            {cp.gruppe && <span>{cp.gruppe}</span>}
                            {cp.norm && <span>· {cp.norm}</span>}
                            {cp.sicherheitsrelevant && <Badge variant="outline" className="border-red-500/40 text-[9px] text-red-700">sicherheitsrelevant</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {STATUS_OPTS.map((o) => (
                          <button key={o.key} type="button" data-testid={`checklist-status-${i}-${o.key}`}
                            onClick={() => setCp(cp.id, { status: o.key })}
                            className={`rounded-md border px-2 py-1 text-xs transition-colors ${state[cp.id]?.status === o.key ? o.cls + " font-semibold" : "border-border text-muted-foreground hover:bg-accent"}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                      {state[cp.id]?.status === "mangel" && (
                        <div className="mt-2 space-y-1.5">
                          <Textarea rows={2} placeholder="Bemerkung zum Mangel …" data-testid={`checklist-note-${i}`}
                            value={state[cp.id]?.bemerkung || ""} onChange={(e) => setCp(cp.id, { bemerkung: e.target.value })} />
                          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent" data-testid={`checklist-photo-${i}`}>
                            <Camera size={13} /> Foto {state[cp.id]?.foto_media_ids?.length ? `(${state[cp.id].foto_media_ids.length})` : ""}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && addPhoto(cp.id, e.target.files[0])} />
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="checklist-cancel">Abbrechen</Button>
          <Button onClick={submit} disabled={busy || loading || checkpoints.length === 0} className="gap-2" data-testid="checklist-submit">
            <CheckCircle size={16} weight="bold" /> {busy ? "Speichern …" : "Prüfung speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
